/**
 * AudioBus — layered audio via Howler.js.
 *
 * Two ideas that matter for this genre:
 *  1. Layered ambience: a base drone + an intensity layer whose volume tracks
 *     combat intensity, cross-faded so mining feels calm and battles feel loud.
 *  2. Mobile autoplay policy: browsers block audio until a user gesture, so we
 *     stay muted until `unlock()` is called from the first tap/click.
 *
 * Assets are referenced by path under /public/audio and are OPTIONAL — the bus
 * no-ops gracefully if a sound isn't present yet, so the game runs silent until
 * you drop files in.
 */
import { Howl, Howler } from "howler";

type LayerName = "ambientBase" | "ambientCombat";

export class AudioBus {
  private layers = new Map<LayerName, Howl>();
  private sfx = new Map<string, Howl>();
  private unlocked = false;

  /** Register looping ambience layers (safe if files are missing). */
  registerAmbience(paths: Partial<Record<LayerName, string>>): void {
    for (const [name, src] of Object.entries(paths)) {
      if (!src) continue;
      this.layers.set(
        name as LayerName,
        new Howl({ src: [src], loop: true, volume: 0, html5: false }),
      );
    }
  }

  registerSfx(name: string, src: string): void {
    this.sfx.set(name, new Howl({ src: [src] }));
  }

  /** Call from the first user gesture to satisfy mobile autoplay rules. */
  unlock(): void {
    if (this.unlocked) return;
    this.unlocked = true;
    for (const l of this.layers.values()) if (!l.playing()) l.play();
  }

  /** intensity 0..1 cross-fades calm <-> combat ambience. */
  setCombatIntensity(intensity: number): void {
    const t = clamp(intensity, 0, 1);
    this.layers.get("ambientBase")?.volume(0.6 * (1 - 0.5 * t));
    this.layers.get("ambientCombat")?.volume(0.8 * t);
  }

  play(name: string, volume = 1): void {
    if (!this.unlocked) return;
    const s = this.sfx.get(name);
    if (!s) return;
    const id = s.play();
    s.volume(volume, id);
  }

  setMasterVolume(v: number): void {
    Howler.volume(clamp(v, 0, 1));
  }
}

function clamp(v: number, lo: number, hi: number): number {
  return v < lo ? lo : v > hi ? hi : v;
}
